<template>
  <b-modal
    :id="id"
    hide-header
    hide-footer
    :size="existsUser ? 'custom' : 'sm'"
    :modal-class="{ 'favorite-modal': existsUser }"
    :body-class="{ 'px-0 pt-2 pb-4': existsUser }"
    centered
    @show="shown = true"
    @hide="shown = false"
  >
    <template #default="{ close }">
      <b-row class="favorite-header" :class="{ divider: existsUser }">
        <div class="favorite-label">
          {{ existsUser ? "いいね！" : "まだいいね！されていません" }}
        </div>
        <div class="d-flex ml-auto" @click="close"><b-icon-x class="x-btn"></b-icon-x></div>
      </b-row>
      <b-row class="favorite-body">
        <user-list :userList="sortedUserList" />
      </b-row>
    </template>
  </b-modal>
</template>

<style lang="scss" scoped>
::v-deep .favorite-modal {
  .modal-dialog {
    max-width: calc(100vw - 1rem);
    width: calc(100vw - 1rem);
    @media (min-width: 768px) {
      width: 520px;
    }
  }
}
.favorite-header {
  margin: auto 0;
  padding: 0 1.5rem;
  &.divider {
    border-bottom: 1px solid #c9c9c9;
  }
}
.favorite-label {
  margin: auto 0;
  font-size: 13px;
  font-weight: bold;
}
.favorite-body {
  margin: auto 0;
  overflow-x: hidden;
  overflow-y: auto;
}

// PC用画面
@media (min-width: 768px) {
  .favorite-body {
    padding: 0 1.5rem;
  }
}
</style>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import type Vue from "vue";
import {
  useAppStore,
  useCommentStore,
  useMessageStore,
  useReactionStore,
  useUserStore,
} from "@/store";
import { IconReactionType } from "@/API";
import UserList from "./UserList.vue";
import type { FavoriteModalUser } from "../model";
import { User } from "../model";
import { Message, Comment } from "../model";

export default defineComponent({
  name: "favorite-modal",
  components: {
    UserList,
  },
  props: {
    id: { type: String, default: "", required: true },
    userList: { type: Array as PropType<FavoriteModalUser[]>, default: () => [] },
    // Storybook向け
    show: { type: Boolean, default: false },
    messageId: { type: String, default: "", required: true }, //!< 投稿ID
    commentId: { type: String, default: "" }, //!< コメントID
  },
  setup(props) {
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const shown = ref<boolean>(false); // モーダルの開閉状態
    const userListSource = ref<FavoriteModalUser[]>([]);

    // Vuexの変更監視
    let unsubscribeMutation: (() => void) | undefined;

    const sortedUserList = computed((): FavoriteModalUser[] => {
      // ソート部分
      return userListSource.value;
    });

    const existsUser = computed((): boolean => {
      return !!sortedUserList.value.length;
    });

    watch(
      () => props.userList,
      (): void => {
        userListSource.value = props.userList;
      },
      { immediate: true }
    );

    // Lifecycle
    onMounted((): void => {
      // Storybook
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }

      // 旧 Vuex の reactions/set・messages/add・comments/add mutation 監視を
      // 各ストアの state 変化監視（$subscribe）へ移植。
      // これらの mutation は action / GraphQL サブスクリプション双方から発火していたため、
      // $onAction では捕捉しきれない。state を真実源として再評価する。
      const reactionStore = useReactionStore();
      const messageStore = useMessageStore();
      const commentStore = useCommentStore();

      // お気に入り(LIKE)ユーザー一覧を reactions ストアから再計算する
      const recomputeLikeUsers = (): void => {
        if (!shown.value) {
          return;
        }
        const domainId = useAppStore().domainId;
        const topicId = useAppStore().topicId;
        const users = useUserStore().getByDomainId(domainId) as User[];
        const target = props.commentId
          ? { domainId, topicId, messageId: props.messageId, commentId: props.commentId }
          : { domainId, topicId, messageId: props.messageId };
        const reaction = reactionStore.get(IconReactionType.LIKE, target as never);
        const userList = reaction ? reaction.userList : [];
        userListSource.value = userList.map((u) => {
          const user =
            users.find((user) => user.directId == u.userId) ?? User.createNotFoundUser(u.userId);
          return { user: user, updatedAt: u.updatedAt } as FavoriteModalUser;
        });
      };
      const unsubReaction = reactionStore.$subscribe(() => {
        recomputeLikeUsers();
      });

      // 投稿が削除された時、モーダルを閉じる (*エラーモーダルは今入れてない)
      const unsubMessage = messageStore.$subscribe(() => {
        if (!shown.value) {
          return;
        }
        const list = messageStore.get(useAppStore().domainId, useAppStore().topicId) || [];
        const msg = list.find((m) => m.id == props.messageId) as Message | undefined;
        if (msg && msg.deleted == true) {
          vm?.$bvModal.hide(props.id);
        }
      });

      // コメントが削除された時、モーダルを閉じる (*エラーモーダルは今入れてない)
      const unsubComment = commentStore.$subscribe(() => {
        if (!shown.value) {
          return;
        }
        if (!props.commentId) {
          return;
        }
        const list = commentStore.get(useAppStore().topicId, props.messageId) || [];
        const comment = list.find((c) => c.id == props.commentId) as Comment | undefined;
        if (comment && comment.deleted == true && comment.messageId == props.messageId) {
          vm?.$bvModal.hide(props.id);
        }
      });

      unsubscribeMutation = () => {
        unsubReaction();
        unsubMessage();
        unsubComment();
      };
    });

    // Lifecycle
    onBeforeUnmount((): void => {
      if (unsubscribeMutation) unsubscribeMutation();
    });

    return {
      shown,
      userListSource,
      sortedUserList,
      existsUser,
    };
  },
});
</script>
